import React from 'react';
import { CheckCircle, AlertTriangle, TrendingDown, Clock } from 'lucide-react';

export default function AutonomyMetrics() {
  const metrics = {
    autonomyScore: 96,
    autonomousDecisions: 1247,
    escalations: 52,
    avgEscalationTime: '2.3h',
    falsePositives: '0.6%',
    userSatisfaction: 4.3,
    weeklyTrend: [
      { week: 'Week 1', autonomy: 87, escalations: 156 },
      { week: 'Week 2', autonomy: 91, escalations: 98 },
      { week: 'Week 3', autonomy: 94, escalations: 72 },
      { week: 'Week 4', autonomy: 96, escalations: 52 },
    ],
    escalationReasons: [
      { reason: 'User approval needed', count: 28, pct: '54%' },
      { reason: 'Amount threshold exceeded', count: 16, pct: '31%' },
      { reason: 'New supplier/unknown', count: 8, pct: '15%' },
    ],
  };

  const ProgressRing = ({ percentage, label, color = 'green' }) => {
    const colorClass = {
      green: 'text-green-400',
      blue: 'text-[#2E6BFF]',
      yellow: 'text-yellow-400',
    }[color];

    const circumference = 2 * Math.PI * 45;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
      <div className="flex flex-col items-center">
        <div className="relative w-32 h-32">
          <svg className="w-full h-full transform -rotate-90">
            <circle
              cx="64"
              cy="64"
              r="45"
              stroke="rgba(255,255,255,0.1)"
              strokeWidth="8"
              fill="none"
            />
            <circle
              cx="64"
              cy="64"
              r="45"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              className={colorClass}
              style={{
                strokeDasharray: circumference,
                strokeDashoffset,
                strokeLinecap: 'round',
              }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-3xl font-bold ${colorClass}`}>{percentage}%</span>
            <span className="text-white/50 text-xs">{label}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Agent Autonomy Metrics</h2>
        <p className="text-white/60">
          How well agents handle decisions independently vs. escalating to humans
        </p>
      </div>

      {/* Main Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        <div className="flex flex-col items-center">
          <ProgressRing percentage={metrics.autonomyScore} label="Autonomy Score" color="green" />
        </div>

        <div className="bg-white/5 border border-white/10 rounded-lg p-6 flex flex-col justify-center">
          <p className="text-white/60 text-sm mb-2">Autonomous Decisions</p>
          <p className="text-3xl font-bold text-white mb-2">{metrics.autonomousDecisions.toLocaleString()}</p>
          <p className="text-green-400 text-sm">This week</p>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-lg p-6 flex flex-col justify-center">
          <p className="text-white/60 text-sm mb-2">Escalations</p>
          <p className="text-3xl font-bold text-white mb-2">{metrics.escalations}</p>
          <p className="text-yellow-400 text-sm">Avg {metrics.avgEscalationTime} to handle</p>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-lg p-6 flex flex-col justify-center">
          <p className="text-white/60 text-sm mb-2">False Positive Rate</p>
          <p className="text-3xl font-bold text-green-400 mb-2">{metrics.falsePositives}</p>
          <p className="text-white/40 text-sm">Very low error rate</p>
        </div>
      </div>

      {/* Autonomy Trend */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
          <TrendingDown size={20} className="text-green-400" />
          Autonomy Trend (Week 1 → 4)
        </h3>
        <div className="space-y-4">
          {metrics.weeklyTrend.map((week) => (
            <div key={week.week}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-white text-sm font-medium">{week.week}</p>
                <div className="flex items-center gap-4">
                  <p className="text-green-400 text-sm">{week.autonomy}% autonomous</p>
                  <p className="text-white/50 text-sm">{week.escalations} escalations</p>
                </div>
              </div>
              <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-green-400 h-full rounded-full"
                  style={{ width: `${week.autonomy}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Escalation Reasons */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
          <AlertTriangle size={20} className="text-yellow-400" />
          Why Agents Escalate
        </h3>
        <div className="space-y-4">
          {metrics.escalationReasons.map((item) => (
            <div key={item.reason}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-white text-sm">{item.reason}</p>
                <p className="text-white/60 text-sm">{item.count} cases ({item.pct})</p>
              </div>
              <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-yellow-400 h-full rounded-full"
                  style={{ width: item.pct }}
                />
              </div>
            </div>
          ))}
        </div>
        <p className="text-white/50 text-xs mt-4">
          These are intentional escalations, not failures. Agents know when they need human judgment.
        </p>
      </div>

      {/* Quality Score */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white/5 border border-white/10 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <CheckCircle size={20} className="text-green-400" />
            User Satisfaction
          </h3>
          <p className="text-4xl font-bold text-green-400 mb-2">{metrics.userSatisfaction}/5.0</p>
          <div className="space-y-2 text-sm">
            <p className="text-green-400">✓ Very helpful: 78%</p>
            <p className="text-white/70">○ Helpful: 18%</p>
            <p className="text-white/50">△ Could improve: 4%</p>
          </div>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Clock size={20} className="text-[#2E6BFF]" />
            Response Times
          </h3>
          <div className="space-y-3 text-sm">
            <div>
              <p className="text-white/60">Dashboard messages</p>
              <p className="text-white font-semibold">30 sec avg</p>
            </div>
            <div>
              <p className="text-white/60">WhatsApp messages</p>
              <p className="text-white font-semibold">2.3h avg</p>
            </div>
            <div>
              <p className="text-white/60">User escalation response</p>
              <p className="text-white font-semibold">&lt;4h typical</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
